import { useEffect, useRef, useState } from "react";
import { crmMates, ensureWriter, knownWriterId } from "./api";
import { currentUserId } from "./auth";
import { bodyWithCredit, coffreLocked, loadFicheNotes, noteStamp, noteVisibleTo, peekFicheNotes, saveFicheNotes, type CrmNote, type NoteLabel } from "./coffre";
import { CoffreLockedLine, useCoffreUnlocked } from "./CoffreUnlock";
import { aimedFind, clearFind } from "./find";
import { saveSuivi, type SuiviTask } from "./suivi";
import { useLive } from "./Suivi";
import { Modal } from "./Modal";
import { DateField } from "./pickers";
import { NotePad } from "./NotePad";
import { PlaceSheet, type NoteOrigin } from "./PlaceSheet";
import { loadCrmWeek, setWeekTaskDone } from "./week";

type Tone = { id: string; name: string; color: string };

/** Pastilles purement internes aux notes fiche — pas le statut Kanban / Mes tâches. */
const FALLBACK_NOTE_LABELS: Tone[] = [
  { id: "todo", name: "À faire", color: "#64748b" },
  { id: "inprogress", name: "En cours", color: "#2563eb" },
  { id: "pending", name: "En attente", color: "#d97706" },
  { id: "rdv", name: "RDV", color: "#1d4ed8" },
  { id: "important", name: "Important", color: "#e11d48" },
  { id: "suivi", name: "À suivre", color: "#d97706" },
  { id: "call", name: "Appeler", color: "#7c3aed" },
  { id: "quote", name: "Devis", color: "#0f766e" },
  { id: "unpaid", name: "Impayé", color: "#c2410c" },
];

export function NoteBlock({ folder, ownerId = 0, ficheId = 0, origin }: { folder: string; ownerId?: number; ficheId?: number; origin?: NoteOrigin }) {
  const [notes, setNotes] = useState<CrmNote[]>([]);
  const [id, setId] = useState("");
  const [heading, setHeading] = useState("");
  const [text, setText] = useState("");
  const [draftLabel, setDraftLabel] = useState<NoteLabel>("");
  const [draftDue, setDraftDue] = useState("");
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [reload, setReload] = useState(0);
  const [place, setPlace] = useState(false);
  const [info, setInfo] = useState(false);
  const [labelFor, setLabelFor] = useState("");
  const [loading, setLoading] = useState(true);
  const [placed, setPlaced] = useState("");
  const [placeDay, setPlaceDay] = useState("");
  const [pending, setPending] = useState<CrmNote | "new" | null>(null);
  const [editing, setEditing] = useState<CrmNote | null>(null);
  const [editText, setEditText] = useState("");
  const [pad, setPad] = useState(0);
  const [rich, setRich] = useState(() => window.localStorage.getItem("crm_note_rich") === "1");
  const [labels, setLabels] = useState<Tone[]>(FALLBACK_NOTE_LABELS);
  const clickWait = useRef(0);
  const savedTimer = useRef(0);
  const [saved, setSaved] = useState(false);
  const [dropNote, setDropNote] = useState<CrmNote | null>(null);
  const [archives, setArchives] = useState(false);
  const live = useLive();
  const guest = ownerId > 0;
  const [meId, setMeId] = useState(() => knownWriterId());
  const [seenTaskIds, setSeenTaskIds] = useState<Set<string> | null>(guest ? null : new Set());
  const knownFile = useRef(false);
  const chain = useRef(Promise.resolve());
  const generation = useRef(0);
  useCoffreUnlocked(() => setReload((value) => value + 1));
  useEffect(() => {
    void ensureWriter().then((who) => setMeId(who.id || knownWriterId()));
  }, []);

  useEffect(() => {
    let alive = true;
    void loadCrmWeek()
      .then((board) => {
        if (!alive) return;
        const rows = (board.note_labels || [])
          .filter((item) => item.key && item.key !== "done" && item.key !== "fait")
          .map((item) => ({
            id: item.key,
            name: item.label || item.key,
            color: item.color?.bg && /^#[0-9a-fA-F]{6}$/.test(item.color.bg) ? item.color.bg : "#64748b",
          }));
        if (rows.length > 0) setLabels(rows);
        if (ownerId > 0) setSeenTaskIds(new Set([...board.tasks, ...board.kanbanShared].map((task) => task.id)));
      })
      .catch(() => {
        if (alive && ownerId > 0) setSeenTaskIds(new Set());
      });
    return () => { alive = false; };
  }, [ownerId]);

  const ficheKey = useRef("");
  useEffect(() => {
    const mine = generation.current + 1;
    generation.current = mine;
    const moved = ficheKey.current !== `${folder}|${ownerId}`;
    ficheKey.current = `${folder}|${ownerId}`;
    const snap = peekFicheNotes(folder, ownerId);
    knownFile.current = Boolean(snap?.exists);
    setNotes(snap?.notes || []);
    if (moved) {
      setId("");
      setHeading("");
      setText("");
      setLabelFor("");
    }
    setError("");
    setLoading(!snap);
    let alive = true;
    void loadFicheNotes(folder, ownerId)
      .then((loaded) => {
        if (!alive || generation.current !== mine) return;
        knownFile.current = loaded.exists;
        setLocked(false);
        const signed = loaded.notes.map((row) => signLoaded(row));
        setNotes(signed);
        if (signed.some((row, index) => row !== loaded.notes[index])) store(signed, "Signature non enregistrée");
      })
      .catch((reason: unknown) => {
        if (alive && generation.current === mine && !snap) {
          setLocked(coffreLocked(reason));
          setError(reason instanceof Error ? reason.message : "Notes indisponibles");
        }
      })
      .finally(() => {
        if (alive && generation.current === mine) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [folder, ownerId, reload]);

  useEffect(() => {
    const open = () => {
      const hit = aimedFind("Note", ficheId);
      if (!hit || notes.length === 0) return;
      const note = notes.find((row) => (hit.noteHeading ? row.heading === hit.noteHeading : (row.heading.trim() || row.body.trim().slice(0, 80)) === hit.title));
      if (!note || !noteVisibleTo(note, Number(currentUserId()) || 0, ownerId > 0, ownerId > 0 ? seenTaskIds : null)) return;
      show(note);
      clearFind();
    };
    open();
    window.addEventListener("crm-find", open);
    return () => window.removeEventListener("crm-find", open);
  }, [notes, ficheId, ownerId, seenTaskIds]);

  function signLoaded(row: CrmNote): CrmNote {
    const viewer = Number(currentUserId()) || 0;
    const authorId = row.authorId || 0;
    if (authorId <= 0 || authorId === viewer) return row;
    const name = row.author || crmMates().find((mate) => mate.id === authorId)?.name || live.suivi?.people.find((person) => person.id === authorId)?.name || "";
    if (!name) return row;
    const body = bodyWithCredit(row.body, name);
    if (row.author === name && body === row.body.trim()) return row;
    return { ...row, author: name, body };
  }

  function store(snapshot: CrmNote[], fail: string) {
    const mine = generation.current;
    const here = folder;
    const who = ownerId;
    const exists = knownFile.current;
    chain.current = chain.current
      .then(() => saveFicheNotes(here, snapshot, exists, who))
      .then(() => {
        if (generation.current === mine) knownFile.current = true;
      })
      .catch((reason: unknown) => {
        if (generation.current === mine) setError(reason instanceof Error ? reason.message : fail);
      });
  }

  function dirty(): boolean {
    const note = text.trim();
    if (!note) return false;
    if (!id) return true;
    const current = notes.find((row) => row.id === id);
    return !current || current.body.trim() !== note;
  }

  function blank() {
    setId("");
    setHeading("");
    setText("");
    setDraftLabel("");
    setDraftDue("");
    setError("");
    setRich(window.localStorage.getItem("crm_note_rich") === "1");
    setPad((value) => value + 1);
  }

  function show(note: CrmNote) {
    setError("");
    setId(note.id);
    setHeading(note.heading);
    setText(shownBody(note));
    setDraftLabel(note.label);
    setDraftDue(live.suivi?.tasks.find((item) => item.note_key === note.heading)?.due_on || "");
    setRich(noteIsRich(note.body));
    setPad((value) => value + 1);
  }

  function edit(note: CrmNote) {
    window.clearTimeout(clickWait.current);
    setEditText(shownBody(note));
    setRich(noteIsRich(note.body));
    setEditing(note);
  }

  function saveEdit(close: () => void) {
    if (!editing) return;
    const body = editing.author ? bodyWithCredit(editText.trim(), editing.author) : editText.trim();
    if (!body) return;
    const next = notes.map((row) => (row.id === editing.id ? { ...row, body } : row));
    setNotes(next);
    if (id === editing.id) {
      setText(body);
      setPad((value) => value + 1);
    }
    store(next, "Note non enregistrée");
    close();
    setId("");
    setHeading("");
    setText("");
    setPad((value) => value + 1);
    pulse();
  }

  function pulse() {
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 1400);
  }

  function openSaved(note: CrmNote) {
    if (note.id === id && !dirty()) return;
    if (!dirty()) {
      show(note);
      return;
    }
    setPending(note);
  }

  function openNew() {
    if (!text.trim() && !id) return;
    if (!dirty()) {
      blank();
      return;
    }
    setPending("new");
  }

  function keepGoing(save: boolean, close: () => void) {
    const next = pending;
    close();
    if (!next) return;
    if (save) persist();
    if (next === "new") blank();
    else show(next);
  }

  async function persist(openPlace = false): Promise<boolean> {
    const note = text.trim();
    if (!note) return false;
    const who = await ensureWriter();
    const meId = who.id || Number(currentUserId()) || 0;
    const mine = who.name || crmMates().find((mate) => mate.id === meId)?.name || live.suivi?.people.find((person) => person.id === meId)?.name || "";
    const current = id ? notes.find((row) => row.id === id) : undefined;
    const author = current?.author || (!current && guest ? mine : "");
    const authorId = current ? (current.authorId || 0) : (guest ? meId : 0);
    const body = author ? bodyWithCredit(note, author) : note;
    const stamp = noteStamp();
    const saved = id
      ? notes.map((row) => (row.id === id ? { ...row, body, label: draftLabel, author, authorId } : row))
      : [...notes, { id: stamp, heading: stamp, body, label: draftLabel, author, authorId }];
    const written = saved.find((row) => row.id === (id || stamp));
    setNotes(saved);
    setId("");
    setHeading("");
    setText("");
    setDraftLabel("");
    setDraftDue("");
    setError("");
    setPad((value) => value + 1);
    if (openPlace) {
      setPlaced(note);
      setPlaceDay(draftDue);
      setPlace(true);
    }
    store(saved, "Note non enregistrée");
    const linked = written ? live.suivi?.tasks.find((item) => item.note_key === written.heading) : undefined;
    if (written && (draftLabel || linked)) linkTask(written, draftLabel || labelFromKind(linked?.kind), draftDue);
    pulse();
    return true;
  }

  function owns(row: CrmNote): boolean {
    if (!guest) return true;
    const mine = meId || knownWriterId();
    return mine > 0 && (row.authorId || 0) === mine;
  }

  function drop(noteId: string) {
    const row = notes.find((item) => item.id === noteId);
    if (!row || !owns(row)) return;
    const next = notes.filter((row) => row.id !== noteId);
    setNotes(next);
    if (id === noteId) {
      setId("");
      setHeading("");
      setText("");
    }
    setError("");
    store(next, "Note non supprimée");
  }

  function tag(noteId: string, label: NoteLabel) {
    const row = notes.find((item) => item.id === noteId);
    const next = notes.map((item) => (item.id === noteId ? { ...item, label } : item));
    setNotes(next);
    setLabelFor("");
    store(next, "Label non enregistré");
    if (row && label) linkTask(row, label);
  }

  function linkTask(row: CrmNote, label: NoteLabel, due?: string) {
    if (ficheId <= 0 || !label) return;
    const kind = label === "rdv" ? "meeting" : label === "quote" ? "quote" : label === "unpaid" ? "unpaid" : "call";
    const dueOn = due !== undefined ? due : (live.suivi?.tasks.find((item) => item.note_key === row.heading)?.due_on || "");
    void saveSuivi(ficheId, { action: "sync", note_key: row.heading, title: firstLine(row.body).slice(0, 180), kind, done: isDoneLabel(label), due_on: dueOn })
      .then(() => window.dispatchEvent(new Event("crm-suivi-refresh")))
      .catch(() => setError("La tâche liée n’a pas été mise à jour"));
  }

  useEffect(() => {
    const onLabel = (event: Event) => {
      const detail = (event as CustomEvent<{ key?: string; label?: NoteLabel }>).detail;
      if (!detail?.key || !detail.label) return;
      setNotes((current) => {
        const row = current.find((item) => item.heading === detail.key);
        if (!row || row.label === detail.label) return current;
        const next = current.map((item) => (item.heading === detail.key ? { ...item, label: detail.label || "" } : item));
        store(next, "Label non enregistré");
        return next;
      });
    };
    window.addEventListener("crm-note-label", onLabel);
    return () => window.removeEventListener("crm-note-label", onLabel);
  }, [folder, ownerId]);

  function taskOf(row: CrmNote): SuiviTask | undefined {
    return live.suivi?.tasks.find((item) => item.note_key === row.heading);
  }

  function noteDone(row: CrmNote): boolean {
    return Boolean(taskOf(row)?.done) || isDoneLabel(row.label);
  }

  const viewerId = meId || knownWriterId();
  const ordered = [...notes].reverse().filter((row) => noteVisibleTo(row, viewerId, guest, guest ? seenTaskIds : null));
  function personName(row: CrmNote): string {
    return row.author || crmMates().find((mate) => mate.id === row.authorId)?.name || live.suivi?.people.find((person) => person.id === row.authorId)?.name || "";
  }
  function credit(row: CrmNote): string {
    const named = personName(row);
    const sharedTask = Boolean(guest && row.taskId && seenTaskIds?.has(row.taskId) && (row.authorId || 0) !== viewerId);
    if ((row.authorId || 0) === viewerId && viewerId > 0 && !sharedTask) return "";
    if ((row.authorId || 0) > 0 || row.author || sharedTask) {
      return `De la part de ${named || (sharedTask ? crmMates().find((mate) => mate.id === ownerId)?.name || "l’auteur" : "un collègue")}`;
    }
    return "";
  }
  function shownBody(row: CrmNote): string {
    const name = personName(row);
    return name ? bodyWithCredit(row.body, name) : row.body;
  }
  const opened = notes.find((row) => row.id === id);
  const openCredit = opened ? credit(opened) : "";
  const openNotes = ordered.filter((row) => !noteDone(row));
  const archivedNotes = ordered.filter((row) => noteDone(row));
  const shownNotes = archives ? [...openNotes, ...archivedNotes] : openNotes;
  const openCount = openNotes.length;
  const archivedCount = archivedNotes.length;
  const frameTone = draftLabel && !isDoneLabel(draftLabel) ? toneOf(labels, draftLabel) : undefined;

  return (
    <div className={rich ? "crm-card crm-note-card is-rich relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]" : "crm-card crm-note-card relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]"}>
      <div className="crm-head flex shrink-0 items-center border-b px-4 py-2 text-sm font-semibold">
        <h2>Note</h2>
        <span className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            className="crm-pill rounded-full px-3 py-1 text-xs font-semibold"
            onClick={openNew}
          >
            Nouvelle
          </button>
          <button type="button" className="crm-pill grid h-7 w-7 place-items-center rounded-full" aria-label="À propos des notes" onClick={() => setInfo(true)}>
            <InfoIcon />
          </button>
        </span>
      </div>
      {locked ? <CoffreLockedLine className="border-b px-4 py-2 text-sm text-slate-600" /> : null}
      <div className="flex min-h-0 flex-1 gap-4 p-4">
      <div className="flex min-w-0 flex-1 flex-col">
      <button
        type="button"
        role="switch"
        aria-checked={rich}
        className="crm-pill mb-2 flex w-fit items-center gap-2 rounded-full py-1 pl-3 pr-1 text-xs font-semibold"
        onClick={() => {
          setRich((value) => {
            const next = !value;
            window.localStorage.setItem("crm_note_rich", next ? "1" : "0");
            return next;
          });
          setPad((value) => value + 1);
        }}
      >
        {rich ? "Enrichie" : "Simple"}
        <span className={rich ? "crm-fill relative h-5 w-9 rounded-full" : "relative h-5 w-9 rounded-full bg-slate-300"}>
          <span className={rich ? "absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-white" : "absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white"} />
        </span>
      </button>
      <div className={frameTone ? "crm-note-frame is-labeled relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl" : "crm-note-frame relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200"}>
      {frameTone ? <span className="crm-note-frame-ring" style={{ borderColor: frameTone.color }} aria-hidden /> : null}
      {openCredit ? <p className="px-3 pt-2 text-xs font-semibold text-slate-500">{openCredit}</p> : null}
      {rich ? <NotePad key={pad} initial={text} upload={folder ? { folder, ownerId } : null} onChange={setText} /> : (
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              persist();
            }
          }}
          placeholder="Écrire la note…"
          className="crm-field min-h-0 w-full flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm outline-none"
        />
      )}
      </div>
      <div className="mt-2 flex shrink-0 flex-wrap items-center gap-2">
        {labels.filter((item) => !isDoneLabel(item.id)).map((item) => (
          <button key={item.id} type="button" className={draftLabel === item.id ? "rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" : "rounded-full px-2 py-0.5 text-[10px] font-semibold"} style={draftLabel === item.id ? { background: item.color } : { color: item.color, boxShadow: `inset 0 0 0 1px ${item.color}` }} onClick={() => setDraftLabel(draftLabel === item.id ? "" : item.id)}>
            {item.name}
          </button>
        ))}
        <span className="flex shrink-0 items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Pour quand ?</span>
          <span className="w-52"><DateField value={draftDue} onChange={setDraftDue} /></span>
        </span>
      </div>
      <div className="mt-2 flex shrink-0 items-center gap-3">
        <button type="button" disabled={text.trim() === ""} className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" onClick={() => persist()}>
          Enregistrer
        </button>
        <button
          type="button"
          disabled={text.trim() === ""}
          className="crm-pop rounded-full border border-slate-200 px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
          onClick={() => persist(true)}
        >
          Placer
        </button>
        {saved ? <span className="crm-note-saved">Enregistrée</span> : null}
        {error ? <p className="truncate text-sm text-rose-700">{error}</p> : null}
      </div>
      </div>
      <aside className="crm-scroll crm-rule flex min-w-0 w-[42%] shrink flex-col overflow-auto border-l pl-3">
        <div className="flex shrink-0 items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Mes notes</p>
          {archivedCount > 0 ? (
            <button type="button" className="crm-pill ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold" aria-pressed={archives} onClick={() => setArchives((value) => !value)}>
              {archives ? "Masquer les archives" : `Archives ${archivedCount}`}
            </button>
          ) : null}
        </div>
        {ordered.length === 0 ? <p className="mt-2 text-sm text-slate-400">Aucune note.</p> : null}
        {ordered.length > 0 && openCount === 0 && !archives ? <p className="mt-2 text-sm text-slate-400">Aucune note en cours.</p> : null}
        <div className="mt-2 flex flex-col gap-2">
        {shownNotes.map((row, index) => {
          const task = taskOf(row);
          const done = noteDone(row);
          const tone = done ? undefined : toneOf(labels, row.label);
          const line = firstLine(row.body);
          const sub = line && line !== "Note vide" && line !== row.heading.trim() ? line : "";
          const due = !sub && task?.due_on ? task.due_on.split("-").reverse().join("/") : "";
          const from = credit(row);
          const archiveLead = archives && index === openCount && archivedCount > 0;
          return (
          <div key={row.id} className="flex min-w-0 flex-col gap-2">
          {archiveLead ? <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Archives</p> : null}
          <div className="flex min-w-0 items-start gap-1">
            <div
              className={done ? "min-w-0 flex-1 rounded-xl border border-slate-200 px-2 py-1 text-slate-400" : row.id === id ? "min-w-0 flex-1 rounded-xl border crm-soft px-2 py-1" : "crm-note min-w-0 flex-1 rounded-xl border border-transparent px-2 py-1"}
              style={tone ? { borderColor: tone.color } : undefined}
            >
              <div className="flex items-center gap-1">
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => { window.clearTimeout(clickWait.current); clickWait.current = window.setTimeout(() => openSaved(row), 220); }} onDoubleClick={() => edit(row)}>
                  {from ? <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">{from}</span> : null}
                  <span className={done ? "block truncate text-xs font-semibold text-slate-400 line-through" : row.id === id ? "block truncate text-xs font-semibold crm-ink" : "block truncate text-xs font-semibold text-slate-700"}>{row.heading}</span>
                  <span className={done ? "block truncate text-xs text-slate-400 line-through" : "block truncate text-xs text-slate-500"}>{sub || due}</span>
                </button>
                {tone ? (
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: tone.color, boxShadow: `inset 0 0 0 1px ${tone.color}` }}>{tone.name}</span>
                ) : null}
                <button type="button" className="crm-pill shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold" onClick={() => setLabelFor(labelFor === row.id ? "" : row.id)}>
                  {tone ? "Changer" : "Label"}
                </button>
                {task ? (
                  <button type="button" className={task.done ? "shrink-0 rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-500" : "crm-fill shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"} onClick={() => {
                    const next = !task.done;
                    void live.run({ action: "done", task_id: task.id, done: next });
                    window.dispatchEvent(new CustomEvent("crm-note-label", { detail: { key: row.heading, label: next ? "done" : task.kind === "meeting" ? "rdv" : "todo" } }));
                    void setWeekTaskDone(row.taskId || "", next).catch(() => setError("La tâche de la semaine n’a pas été mise à jour"));
                  }}>{task.done ? "Rouvrir" : "Fait"}</button>
                ) : null}
              </div>
              {labelFor === row.id ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {labels.filter((item) => !isDoneLabel(item.id)).map((item) => (
                    <button key={item.id} type="button" className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: item.color, boxShadow: `inset 0 0 0 1px ${item.color}` }} onClick={() => tag(row.id, item.id)}>
                      {item.name}
                    </button>
                  ))}
                  {row.label ? <button type="button" className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-slate-500" onClick={() => tag(row.id, "")}>Aucun</button> : null}
                </div>
              ) : null}
            </div>
            {owns(row) ? (
            <button type="button" aria-label="Supprimer la note" className="mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-400 hover:text-rose-600" onClick={() => setDropNote(row)}>
              <TrashIcon />
            </button>
            ) : null}
          </div>
          </div>
          );
        })}
        </div>
      </aside>
      </div>
      {place ? <PlaceSheet note={placed} origin={origin} initialDay={placeDay} onClose={() => setPlace(false)} /> : null}
      {info ? (
        <Modal onClose={() => setInfo(false)} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Note</p>
              <h2 className="mt-1 text-lg font-semibold">Ce que fait une note</h2>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                <li>Elle suit la fiche quand le dossier change.</li>
                <li>Elle est enregistrée dans CRM clients, dans le dossier de la fiche, fichier notes-CRM.md.</li>
                <li>Vous pouvez la placer dans l’Agenda ou dans Mes tâches. Le texte complet reste dans le Coffre.</li>
                <li>Un label coloré se colle sur la note enregistrée. Le contour suit la même couleur.</li>
                <li>Une note faite est barrée et rangée dans Archives. Le bouton les réaffiche.</li>
              </ul>
            </>
          )}
        </Modal>
      ) : null}
      {editing ? (
        <Modal onClose={() => setEditing(null)} sheetClass={rich ? "crm-pop flex max-h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]" : "crm-pop flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]"}>
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Note</p>
              <h2 className="mt-1 truncate text-lg font-semibold">{editing.heading}</h2>
              <div className="crm-notepad-sheet mt-3 min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200">
                {rich ? <NotePad key={editing.id} initial={editing.body} upload={folder ? { folder, ownerId } : null} onChange={setEditText} /> : <textarea value={editText} onChange={(event) => setEditText(event.target.value)} className="crm-field h-full min-h-[50vh] w-full resize-none rounded-xl border px-3 py-2 text-sm" />}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white" onClick={() => saveEdit(close)}>Enregistrer</button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
      {dropNote ? (
        <Modal onClose={() => setDropNote(null)} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-600">Supprimer</p>
              <h2 className="mt-1 text-lg font-semibold">Supprimer cette note ?</h2>
              <p className="mt-2 text-sm leading-relaxed">{dropNote.heading}. Elle disparaît de la fiche et du fichier notes-CRM.md.</p>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className="crm-pill rounded-full px-4 py-1.5 text-sm font-semibold" onClick={close}>Annuler</button>
                <button type="button" className="rounded-full bg-rose-600 px-4 py-1.5 text-sm font-semibold text-white" onClick={() => { drop(dropNote.id); close(); }}>Supprimer</button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
      {pending ? (
        <Modal onClose={() => setPending(null)} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-600">Note en cours</p>
              <h2 className="mt-1 text-lg font-semibold">Le texte en cours n’est pas enregistré</h2>
              <p className="mt-2 text-sm leading-relaxed">Si vous ouvrez une autre note maintenant, ce que vous êtes en train d’écrire sera perdu.</p>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button type="button" className="crm-pill rounded-full px-4 py-1.5 text-sm font-semibold" onClick={close}>Rester</button>
                <button type="button" className="rounded-full border border-rose-200 px-4 py-1.5 text-sm font-semibold text-rose-700" onClick={() => keepGoing(false, close)}>Continuer sans enregistrer</button>
                <button type="button" className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white" onClick={() => keepGoing(true, close)}>Enregistrer et continuer</button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
      {loading ? (
        <div className="crm-browse-loading" role="status">
          <span className="crm-browse-loading-spin" aria-hidden />
          <span>Chargement des notes</span>
        </div>
      ) : null}
    </div>
  );
}

function noteIsRich(body: string): boolean {
  if (body.includes("<!--BLOCKS_JSON")) return true;
  const visible = body.replace(/<!--[\s\S]*?-->/g, "").trim();
  return /(^|\n)#{1,6}\s+\S|(^|\n)\s*([-*+]|\d+\.)\s+\S|\*\*\S|\[[^\]]+\]\([^)]+\)|(^|\n)>\s+\S/.test(visible);
}

function labelFromKind(kind?: string): NoteLabel {
  if (kind === "meeting") return "rdv";
  if (kind === "quote") return "quote";
  if (kind === "unpaid") return "unpaid";
  return "todo";
}

function isDoneLabel(label: string): boolean {
  return label === "done" || label === "fait";
}

function toneOf(labels: Tone[], id: string): Tone | undefined {
  return labels.find((item) => item.id === id);
}

function firstLine(body: string): string {
  const visible = body.replace(/<!--BLOCKS_JSON[\s\S]*$/, "");
  const line = visible.split("\n").map((part) => part.trim()).find((part) => part && part !== "---" && !part.startsWith("De la part de ")) || "";
  const plain = line.replace(/^#{1,6}\s*/, "").replace(/^[-*]\s+/, "").trim();
  return plain || "Note vide";
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path fill="currentColor" d="M7.3 7.1h1.4V12H7.3V7.1Zm.1-2.5h1.2V5.8H7.4V4.6Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path strokeLinecap="round" d="M9 4.5h6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 7.5h14" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 7.5l.6 11.2a1.4 1.4 0 0 0 1.4 1.3h5a1.4 1.4 0 0 0 1.4-1.3l.6-11.2" />
    </svg>
  );
}

